import React from "react";
import { FileText, Download, ExternalLink, Euro, Info } from "lucide-react";

/**
 * Gebührenordnung-Seite
 * Zeigt die wichtigsten Regelungen und Preise laut aktueller Gebührenordnung
 */
export default function Gebuehrenordnung() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D5C63] via-[#1A7A85] to-[#2D9DA6] py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Gebührenordnung
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Nutzungsgebühren für den Veranstaltungsraum Arenarisquelle in Bad Tabarz
          </p>
        </div>

        {/* Hauptinhalt */}
        <div className="bg-white rounded-xl shadow-2xl p-8 md:p-12">
          {/* PDF Download */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Aktuelle Gebührenordnung (PDF)</h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Die vollständige Gebührenordnung steht als PDF zur Verfügung. Dort finden Sie alle Details zu Preisen und Konditionen.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/documents/Gebührenordnung Vermietung Arenarisquelle.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#0D5C63] hover:bg-[#0A4A50] text-white px-6 py-3 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5" />
                Gebührenordnung herunterladen (PDF)
              </a>
              <a
                href="/documents/Gebührenordnung Vermietung Arenarisquelle.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 px-6 py-3 rounded-lg transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                Im Browser öffnen
              </a>
            </div>
          </section>

          {/* Nutzungsgebühr */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Nutzungsgebühr</h2>
            
            <div className="prose prose-gray max-w-none mb-6">
              <ol className="text-gray-700 space-y-2">
                <li>Die Gebühren werden wie folgt erhoben:</li>
              </ol>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left font-semibold py-3 pr-6 text-gray-900">Nutzung</th>
                    <th className="text-left font-semibold py-3 text-gray-900">Preis</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-100">
                    <td className="py-3 pr-6">Wochenende, ortsansässige Vereine</td>
                    <td className="py-3 font-medium">280,00 €</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 pr-6">Wochenende, Privatpersonen/Unternehmen</td>
                    <td className="py-3 font-medium">380,00 €</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 pr-6">Wochennutzung (4 Stunden)</td>
                    <td className="py-3 font-medium">50,00 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="prose prose-gray max-w-none">
              <ol className="text-gray-700 space-y-2" start={4}>
                <li>
                  Dauernutzungen sind vertraglich gesondert zu vereinbaren. Jede Nutzung beinhaltet die Endreinigung.
                </li>
                <li>
                  Die Nutzungsgebühr ist binnen 14 Tagen nach Rechnungslegung auf das Konto der Kur- und Reiseverkehr Bad Tabarz kAöR zu überweisen.
                </li>
              </ol>
            </div>
          </section>

          {/* Kaution */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">§ 6 Kaution</h2>
            
            <div className="space-y-4 mb-6">
              <ul className="text-gray-700 space-y-2">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                  Bei Wochenendnutzung: Kaution 380,00 €
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                  Bei Abendnutzung: Kaution 50,00 €
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                  Die Kaution wird mit dem Rechnungsbetrag verrechnet, sofern keine Schäden an den Räumlichkeiten oder dem Inventar festgestellt werden.
                </li>
              </ul>
            </div>

            <div className="border-l-4 border-[#0D5C63] pl-4">
              <h3 className="font-semibold text-gray-900 mb-3">Stornierung und Kautionserstattung:</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Absage bis 14 Tage vor Buchung: vollständige Erstattung der Kaution</li>
                <li>• Absage 13 bis 7 Tage vor Veranstaltung: 50% Erstattung der Kaution</li>
                <li>• Absage ab 6 Tage vor Buchung: Kaution verbleibt vollständig bei der KUR</li>
              </ul>
            </div>
          </section>

          {/* Reinigung */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">§ 7 Reinigung</h2>
            
            <ul className="text-gray-700 space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                Die Räumlichkeiten sind vom Nutzer besenrein, bei Geschirrnutzung inkl. gereinigtem Geschirr, zu übergeben.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                Die Endreinigung ist im Nutzungsentgelt enthalten.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                Bei über das übliche Maß hinausgehender Verschmutzung behält sich die KUR vor, eine gesonderte Rechnung auf Stundensatzbasis zu stellen.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#0D5C63] rounded-full mt-2 flex-shrink-0"></span>
                Stundensatz für gesonderte Reinigung: 25,00 € pro Stunde
              </li>
            </ul>
          </section>

          {/* Kontakt */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Fragen zu Preisen und Konditionen?</h2>
            
            <p className="text-gray-700 mb-6">
              Für individuelle Angebote, Sonderkonditionen oder bei Fragen zur Gebührenordnung stehen wir Ihnen gerne zur Verfügung:
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Telefon</h3>
                <p className="text-gray-700">+49 36259 5600</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">E-Mail</h3>
                <p className="text-gray-700">info@bad-tabarz.de</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}